import {
  faArrowUpRightFromSquare,
  faCircleQuestion,
  faFileInvoice,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Text,
  Title,
} from '@mantine/core'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import {
  type CoreUserSubscription,
  type CoreUserSubscriptionCurrentPeriodEstimate,
  type CoreUserSubscriptionInvoice,
} from '@flecto/types'
import {
  Badge,
  DataTable,
  Icon,
  TextCurrency,
  type DataTableColumn,
  type DataTableProps,
} from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'
import { formatDateString, useIntercom } from '@flecto/utils'
import { useLanguage } from '@flecto/utils/src/Hooks/useLanguage'

import styles from './styles.module.css'

type LocalInvoices = CoreUserSubscriptionInvoice & {
  isCurrentPeriod: boolean
}

export type BillingHistoryProps = Omit<DataTableProps<LocalInvoices>, 'columns'> & {
  invoices?: CoreUserSubscriptionInvoice[]
  plan?: CoreUserSubscription['plan']
  currentPeriod?: CoreUserSubscriptionCurrentPeriodEstimate
}

export function BillingHistory({
  invoices,
  plan,
  currentPeriod,
  ...props
}: BillingHistoryProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_billing' })
  const { showIntercomWithMessage } = useIntercom()
  const { isMobile } = useMediaQuery()
  const track = useAnalytics('segment')
  const language = useLanguage()

  const localInvoices: LocalInvoices[] = [
    {
      date: Date.now().toString(),
      total_cents: currentPeriod?.total_cents ?? 0,
      status: 'pending',
      pdf_url: '',
      subscription_cents: currentPeriod?.subscription_cents ?? 0,
      insurance_cents: currentPeriod?.insurance_cents ?? 0,
      isCurrentPeriod: true,
    },
    ...(invoices ?? []).map((item) => ({
      ...item,
      isCurrentPeriod: false,
    })),
  ]

  const isOnlyCurrentPeriod =
    localInvoices[0].insurance_cents === 0 &&
    localInvoices[0].total_cents === 0 &&
    localInvoices[0].subscription_cents === 0 &&
    localInvoices[0].status === 'pending' &&
    !invoices?.length

  const columns: DataTableColumn<LocalInvoices>[] = [
    {
      accessor: 'date',
      title: t('invoice_date'),
      width: isMobile ? 150 : 200,
      render: (row) => {
        const formattedDate = formatDateString({
          date: row.date,
          variant: 'date-full',
        })

        const parts = formattedDate.split(' ')

        return (
          <Text fw={row.isCurrentPeriod ? 500 : undefined} mih={42} lh='42px'>
            {row.isCurrentPeriod
              ? t('invoice_current_month')
              : language === 'en'
                ? `${parts[0]}, ${parts[2]}`
                : `${parts[1]} ${parts[2]}`}
          </Text>
        )
      },
    },
    {
      accessor: 'subscription',
      title: t('invoice_subscription'),
      render: (row) => <TextCurrency cents={row.subscription_cents} />,
    },
    {
      accessor: 'insurance_cost',
      title: t('invoice_insurance_cost'),
      render: (row) =>
        row.insurance_cents === 0 ? (
          '-'
        ) : (
          <TextCurrency cents={row.insurance_cents} />
        ),
    },
    {
      accessor: 'total',
      title: t('invoice_total'),
      render: (row) => <TextCurrency cents={row.total_cents} />,
    },
    {
      accessor: 'status',
      title: t('invoice_status'),
      render: (row) =>
        !row.isCurrentPeriod && (
          <Badge color={row.status === 'paid' ? 'green' : 'red'}>
            {row.status === 'paid' ? t('invoice_paid') : t('invoice_not_paid')}
          </Badge>
        ),
    },
    {
      accessor: 'view_invoice',
      title: '',
      render: (row) =>
        !row.isCurrentPeriod && (
          <Button
            variant='subtle'
            onClick={() => {
              window.open(row.pdf_url, '_blank')
              track('SETTINGS__INSURANCE_DOWNLOAD_INVOICE', {})
            }}
            color='brand'
          >
            <Icon icon={faArrowUpRightFromSquare} mr='sm' />
            {!isMobile && t('invoice_view')}
          </Button>
        ),
    },
  ]

  return (
    <Card w='100%'>
      <Title order={5} mb='sm'>
        {t('billing_history_title')}
      </Title>
      {plan !== 'Starter' && (
        <Text mb='lg' c='gray.7'>
          {t('billing_history_description')}
        </Text>
      )}

      <Box>
        <DataTable
          {...props}
          columns={columns}
          records={
            plan !== undefined && !invoices?.length
              ? []
              : isOnlyCurrentPeriod
                ? []
                : localInvoices
          }
          withBorder={false}
          withPagination
          pinLastColumn={isMobile}
          hideColumns={
            plan !== undefined && !invoices?.length ? [0, 1, 2, 3, 4, 5] : []
          }
          className={styles.table}
          customRowAttributes={({ isCurrentPeriod }) => ({
            'data-isCurrentPeriod': isCurrentPeriod,
          })}
          emptyState={
            <Flex direction='column' gap='sm' align='center'>
              <Icon
                icon={faFileInvoice}
                color='gray.7'
                size='md'
                withCircleBackground
              />
              <Text color='gray.7'>
                {plan === 'Starter'
                  ? t('billing_history_empty_state_title_free')
                  : t('billing_history_empty_state_title')}
              </Text>
            </Flex>
          }
          isLoading={props.isLoading}
        />
      </Box>

      <Card.Section>
        <Divider color='gray.2' />
      </Card.Section>
      <Flex pt='md' gap='sm' align='baseline'>
        <Icon icon={faCircleQuestion} color='gray.7' />
        <Text c='gray.7'>
          {t('invoice_infos')}{' '}
          <Anchor
            component='button'
            onClick={() => showIntercomWithMessage('')}
            c='gray.7'
            fw={500}
            style={{ textDecoration: 'underline' }}
          >
            {t('invoice_contact')}
          </Anchor>
        </Text>
      </Flex>
    </Card>
  )
}
